import { useCallback, useEffect, useState } from 'react'
import {
  isNotDefined,
  Logger,
  UNIVERSAL_SEARCH_SCOPES,
  type UniversalSearchItem,
  type UniversalSearchScope,
} from '@terros/common'
import { type WebSearchScope } from '@hooks'
import { type MaterialSymbolType, useProfile } from '@front/model'
import { isSameSearchResult } from './util'

export type DestinationSearchItem = {
  type: 'Destination'
  path: string
  description: string
  subtitle: string
  icon?: MaterialSymbolType
}

export type SearchResult = UniversalSearchItem | DestinationSearchItem

type RecentResults = {
  recentResults: SearchResult[]
  addResultItem: (result: SearchResult) => void
  clearRecentResults: VoidFunction
}

type StoredSearchItem = SearchResult & { timestamp: number }

const MAX_RECENT_RESULTS = 10
const STORAGE_KEY = 'UNIVERSAL_SEARCH_RECENT_RESULTS'
const RECENT_RESULTS_CHANGED = 'universalSearchRecentResultsChanged'
const ALL_SCOPES = [...UNIVERSAL_SEARCH_SCOPES, 'Destination'] as const

const logger = new Logger('searchResults')
logger.level = 'verbose'

export function useRecentSearchResults(group: WebSearchScope): RecentResults {
  const { user } = useProfile()
  const userId = user?.userId
  const getStorageKey = useCallback(
    (scope: UniversalSearchScope | 'Destination'): string => `${userId}_${STORAGE_KEY}_${scope}`,
    [userId]
  )
  const getStoredResults = useCallback(
    (group: WebSearchScope | 'Destination'): StoredSearchItem[] => {
      if (group === 'all') {
        return ALL_SCOPES.flatMap((scope) => parseResult(localStorage.getItem(getStorageKey(scope))))
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, MAX_RECENT_RESULTS)
      }
      return parseResult(localStorage.getItem(getStorageKey(group === 'destinations' ? 'Destination' : group)))
    },
    [getStorageKey]
  )

  const [recentResults, _setRecentResults] = useState<StoredSearchItem[]>(() => getStoredResults(group))

  useEffect(() => {
    const refreshResults = (): void => _setRecentResults(getStoredResults(group))
    refreshResults()
    window.addEventListener(RECENT_RESULTS_CHANGED, refreshResults)
    return () => window.removeEventListener(RECENT_RESULTS_CHANGED, refreshResults)
  }, [group, getStoredResults])

  const addResultItem = (result: SearchResult): void => {
    const prev = getStoredResults(result.type)
    const prevWithoutItem = prev.filter((item) => !isSameSearchResult(item, result))
    const item: StoredSearchItem = { ...result, timestamp: Date.now() }
    const newResults = [item, ...prevWithoutItem]
    const key = getStorageKey(result.type)
    logger.verbose(`adding result to ${key}`, result)
    localStorage.setItem(key, JSON.stringify(newResults.slice(0, MAX_RECENT_RESULTS)))
    _setRecentResults(getStoredResults(group))
    window.dispatchEvent(new Event(RECENT_RESULTS_CHANGED))
  }

  const clearRecentResults = (): void => {
    const scopes = group === 'all' ? ALL_SCOPES : [group === 'destinations' ? ('Destination' as const) : group]
    scopes.forEach((scope) => localStorage.removeItem(getStorageKey(scope)))
    _setRecentResults([])
    window.dispatchEvent(new Event(RECENT_RESULTS_CHANGED))
  }

  return { recentResults, addResultItem, clearRecentResults }
}

const parseResult = (stored: string | null): StoredSearchItem[] => {
  if (isNotDefined(stored)) return []
  try {
    return JSON.parse(stored) as StoredSearchItem[]
  } catch {
    return []
  }
}
